import { ChangeDetectorRef, Component, Input, ViewChild, ElementRef, OnDestroy, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SitesService } from 'src/app/sites/sites.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from 'src/app/shared/services/common.service';
import { Subscription } from 'rxjs';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';
import moment from 'moment';
import { DeviceCommonService } from '../../../device-common.service';

@Component({
	selector: 'app-charglink-utilization-report',
	templateUrl: './charglink-utilization-report.component.html',
	styleUrls: ['./charglink-utilization-report.component.css']
})
export class CharglinkUtilizationReportComponent implements OnInit, OnChanges, OnDestroy {

	@Input() device: any;

	siteId: number = 0;
	data: any = null;
	devices: any = {};
	siteSub: Subscription = new Subscription();
	getDataSub: Subscription = new Subscription();
	getUtilizationDataSub: Subscription = new Subscription();
	printSub: Subscription = new Subscription();

	periods = this.siteDashboardService.periodsList;
	daysFilters = this.siteDashboardService.daysFilters;
	voltagesOptions: any[] = [];
	manufacturersOptions: any[] = [];
	vintageOptionsDropdown: any[] = this.siteDashboardService.vintageOptionsDropdown;

	selectedPeriod = this.periods[0].value;

	printSelectedPeriod = this.periods[0].value;
	printPeriodText = this.periods[0].label;
	printDateText = moment().format('MM/DD/YYYY');

	@ViewChild('printModal') printModal: any;
	@ViewChild('hiddenPrintBtn') hiddenPrintBtn!: ElementRef<HTMLButtonElement>;

	currentSite: any;
	totalChargers: number = 0;

	printGetUtilizationDataSub: Subscription = new Subscription();
	printDevices: any = {};
	printData: any = null;
	printScoreConfigs: any[] = [];
	printMaxDailyUsage: any = [];
	printAverageDailyUsage: any = [];

	selectedDaysFilter = this.siteDashboardService.daysFilters[0].value;
	selectedVoltage: string = 'all';
	selectedManufacturer: string = 'all';
	selectedVintage = [this.vintageOptionsDropdown[0].value];

	scoreConfigs: any[] = [
		{
			type: 'charger_utilization_score',
			titleKey: 'utilization.utilization_score',
			value: 0,
			color: '',
			status: '',
			dashOffset: 0,
			maxValue: 100,
			tooltipKey: '',
		},
		{
			type: 'charger_ah_score',
			titleKey: 'utilization.ah_score',
			value: 0,
			color: '',
			status: '',
			dashOffset: 0,
			maxValue: 100,
			tooltipKey: '',
		},
		{
			type: 'charger_ws_score',
			titleKey: 'utilization.kwh_score',
			value: 0,
			color: '',
			status: '',
			dashOffset: 0,
			maxValue: 100,
			tooltipKey: '',
		},
	];

	maxDailyUsage: any = [];
	averageDailyUsage: any = [];

	chargerUtilizationScore: any = null;
	chargerAhScore: any = null;
	chargerKwhScore: any = null;
	noData = false;

	operation_weeks: number = 0;

	kwHeatData: any = [];
	kwhHeatData: any = [];

	kwChartOptions: any = {};
	kwhChartOptions: any = {};

	max_charger_daily_kw: number = 0;
	min_charger_daily_kwh: number = 0;

	heatColumns = [
		{ type: 'date', label: 'Week' },
		'',
		{ role: 'style' },
		{ role: 'tooltip', type: 'string', p: { html: true } }
	];

	noDataForPrintMetrics = false;
	noDataForPrintHeat = false;

	constructor(
		private sitesService: SitesService,
		private route: ActivatedRoute,
		private sideMenuService: SideMenuService,
		private translateService: TranslateService,
		private commonService: CommonService,
		public siteDashboardService: SiteDashboardService,
		private cdr: ChangeDetectorRef,
		private deviceCommonService: DeviceCommonService,
	) { }

	getChargersUtilizationData(firstTime = false) {
		const filters = {
			selectedVoltage: this.selectedVoltage,
			selectedManufacturer: this.selectedManufacturer,
		};

		this.getUtilizationDataSub = this.sitesService.getChargersUtilizationData(this.siteId, this.device?.mac_address, filters).subscribe((res: any) => {
			this.data = res.data;
			this.devices = res.devices;

			if (firstTime) {
				this.selectedPeriod = this.periods[0].value;
				this.printSelectedPeriod = this.periods[0].value;
				this.prepareOptions();
			}

			this.prepareUtilizationData();

			// Renders under the OnPush DevicePerformanceAnalyticsComponent: an HTTP response
			// alone never marks the host dirty, so the whole subtree would stay unrendered.
			this.cdr.markForCheck();
		});
	}

	ngOnInit() {
		this.siteSub = this.sideMenuService.currentSite.subscribe((siteInfo: any) => {
			this.currentSite = siteInfo;
			this.commonService.setPageTabTitle(`${siteInfo['customer_name']} - ${siteInfo['name']} - ${this.translateService.instant('g.charglink_site_utilization')}`);
		});

		const tooltipPrefix = this.device?.mac_address ? 'chargLinkDevice.device_charger' : 'chargLinkDevice.site_charger';
		this.scoreConfigs[0].tooltipKey = `${tooltipPrefix}_utilization_score`;
		this.scoreConfigs[1].tooltipKey = `${tooltipPrefix}_ah_score`;
		this.scoreConfigs[2].tooltipKey = `${tooltipPrefix}_kwh_score`;

		this.printSub = this.siteDashboardService.triggerCharglinkPrint.subscribe(() => {
			if (this.device)
				this.openPrintModal();
		});

		this.siteId = this.route?.parent?.snapshot?.params?.siteId || 0;
		this.getChargersUtilizationData(true);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.device && !changes.device.firstChange)
			this.getChargersUtilizationData(true);
	}

	prepareOptions() {
		const voltages: any[] = [];
		const manufacturers: any[] = [];

		for (const macAddress in this.devices) {
			const device = this.devices[macAddress];

			if (!voltages.includes(device.charger_voltage))
				voltages.push(device.charger_voltage);

			if (!manufacturers.includes(device.truck_manufacturer))
				manufacturers.push(device.truck_manufacturer);
		}

		this.voltagesOptions = [
			{ value: 'all', label: this.translateService.instant('g.all') },
			...voltages.map(value => ({ value, label: this.deviceCommonService.getChargerVoltageLabel(value) })),
		];

		this.manufacturersOptions = [{ value: 'all', label: this.translateService.instant('g.all') }, ...manufacturers.map(value => ({ value, label: value }))];
	}

	vintageSelectionChanged() {
		if (!this.selectedVintage.length)
			this.selectedVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];

		if (this.selectedVintage.length > 1 && this.selectedVintage.includes(this.siteDashboardService.vintageOptionsDropdown[0].value)) {
			if (this.selectedVintage[0] == this.siteDashboardService.vintageOptionsDropdown[0].value)
				this.selectedVintage = this.selectedVintage.filter(value => value !== this.siteDashboardService.vintageOptionsDropdown[0].value);
			else
				this.selectedVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];
		}

		this.prepareUtilizationData();
	}

	prepareUtilizationData() {
		const maxDailyAh = (this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter]?.max_daily_as || 0).toFixed(1);
		const maxDailyWs = (this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter]?.max_daily_ws || 0).toFixed(1);
		const maxDailyKw = (this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter]?.max_daily_kw || 0).toFixed(1);

		this.maxDailyUsage = [
			{
				label: this.translateService.instant("utilization.max_daily_ah"),
				value: +maxDailyAh ? 100 : 0,
				displayValue: `${this.commonService.formatNumber(+maxDailyAh, 1)} Ah`,
				colorClass: "copper_orange"
			},
			{
				label: this.translateService.instant("utilization.max_daily_kwh"),
				value: +maxDailyWs ? 100 : 0,
				displayValue: `${this.commonService.formatNumber(+maxDailyWs, 1)} kWh`,
				colorClass: 'copper_orange'
			},
			{
				label: this.translateService.instant("utilization.max_daily_kw"),
				value: +maxDailyKw ? 100 : 0,
				displayValue: `${this.commonService.formatNumber(+maxDailyKw, 1)} kW`,
				colorClass: 'copper_orange'
			}
		];

		const averageDailyAhData = (this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter]?.avg_daily_as || []);
		const averageDailyAh = averageDailyAhData?.reduce((sum: number, device: { value: number; }) => sum + device.value, 0) / 3600;
		const averageDailyAhPercentage = (+maxDailyAh && +averageDailyAh.toFixed(1) ? Math.round(averageDailyAh / +maxDailyAh * 100) : 0);

		const averageDailyWsData = (this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter]?.avg_daily_ws || []);
		const averageDailyWs = averageDailyWsData?.reduce((sum: number, device: { value: number; }) => sum + device.value, 0) / 3600000;
		const averageDailyWsPercentage = (+maxDailyWs && +averageDailyWs.toFixed(1) ? Math.round(averageDailyWs / +maxDailyWs * 100) : 0);

		const averageDailyKwData = (this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter]?.avg_daily_kw || []);
		const averageDailyKw = averageDailyKwData?.reduce((sum: number, device: { value: number; }) => sum + device.value, 0);
		const averageDailyKwPercentage = (+maxDailyKw && +averageDailyKw.toFixed(1) ? Math.round(averageDailyKw / +maxDailyKw * 100) : 0);

		this.averageDailyUsage = [
			{
				label: this.translateService.instant("utilization.avg_daily_ah"),
				value: averageDailyAhPercentage,
				displayValue: `${this.commonService.formatNumber(averageDailyAh, 1)} Ah`,
				colorClass: "cornflower_blue",
				percentage: averageDailyAhPercentage,
				showPercentage: true,
			},
			{
				label: this.translateService.instant("utilization.avg_daily_kwh"),
				value: averageDailyWsPercentage,
				displayValue: `${this.commonService.formatNumber(averageDailyWs, 1)} kWh`,
				colorClass: 'cornflower_blue',
				percentage: averageDailyWsPercentage,
				showPercentage: true,
			},
			{
				label: this.translateService.instant("utilization.avg_daily_kw"),
				value: averageDailyKwPercentage,
				displayValue: `${this.commonService.formatNumber(averageDailyKw, 1)} kW`,
				colorClass: 'cornflower_blue',
				percentage: averageDailyKwPercentage,
				showPercentage: true,
			}
		];

		this.chargerUtilizationScore = (this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter]?.devicesUsageScore || []);
		this.chargerAhScore = (this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter]?.devicesAhScore || []);
		this.chargerKwhScore = (this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter]?.devicesKwhScore || []);

		let { utilization_score, ah_score, kwh_score } = this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter] || {};

		utilization_score = (utilization_score || []);
		ah_score = (ah_score || []);
		kwh_score = (kwh_score || []);

		this.noData = !utilization_score?.length &&
			!ah_score?.length &&
			!kwh_score?.length &&
			!averageDailyAhData.length &&
			!averageDailyWsData.length &&
			!averageDailyKwData.length;
	}

	isIncludedDevice(macAddress: string) {
		if (this.selectedVoltage !== 'all' && this.devices?.[macAddress]?.charger_voltage !== this.selectedVoltage)
			return false;

		if (this.selectedManufacturer !== 'all' && this.devices?.[macAddress]?.truck_manufacturer !== this.selectedManufacturer)
			return false;

		return true;
	}

	openPrintModal() {
		this.printSelectedPeriod = this.periods[0].value;
		this.printModal.show();
	}

	confirmPrint() {
		if (this.device)
			return this.getChargersUtilizationDataForPrint(true);

		if (!this.printData)
			return this.getChargersUtilizationDataForPrint(false);

		this.printContent();
	}

	getChargersUtilizationDataForPrint(getHeatData: boolean = false) {
		const filters: any = {};
		if (getHeatData)
			filters.selectedPeriod = this.printSelectedPeriod;

		// Drop any in-flight print request first, otherwise a slower earlier response can
		// land after a newer one and overwrite printData with the previous period.
		this.printGetUtilizationDataSub?.unsubscribe();

		this.printGetUtilizationDataSub = this.sitesService.getChargersUtilizationData(this.siteId, this.device?.mac_address, filters, getHeatData).subscribe((res: any) => {
			this.printData = res.data;
			this.printDevices = res.devices;

			this.printContent(res.heatData);
		});
	}

	printContent(heatData?: any) {
		const oldestTimestamp = Math.min(...Object.values(this.printDevices).filter((d: any) => d.installation_date).map((d: any) => Number(d.installation_date)));
		const { from, to } = this.commonService.getDateRange(this.printSelectedPeriod, oldestTimestamp, true);
		if (!from || !to)
			this.printPeriodText = '-';
		else
			this.printPeriodText = `${moment(from).format('MM/DD/YYYY')} - ${moment(to).format('MM/DD/YYYY')}`;

		// Clone the page configs but reset the computed state. PerformanceScoreWidget mutates
		// scoreConfigs in place and returns early when the selected period has no bucket, so
		// seeding it with the page's already-computed scores would print the page's period.
		this.printScoreConfigs = this.scoreConfigs.map(config => ({ ...config, value: 0, color: '', status: '', dashOffset: 0 }));

		// Recalculate metrics for print selection
		const maxDailyAh = (this.printData?.[this.printSelectedPeriod]?.[this.siteDashboardService.daysFilters[0].value]?.max_daily_as || 0).toFixed(1);
		const maxDailyWs = (this.printData?.[this.printSelectedPeriod]?.[this.siteDashboardService.daysFilters[0].value]?.max_daily_ws || 0).toFixed(1);
		const maxDailyKw = (this.printData?.[this.printSelectedPeriod]?.[this.siteDashboardService.daysFilters[0].value]?.max_daily_kw || 0).toFixed(1);

		this.printMaxDailyUsage = [
			{
				label: this.translateService.instant("utilization.max_daily_ah"),
				value: +maxDailyAh ? 100 : 0,
				displayValue: `${this.commonService.formatNumber(+maxDailyAh, 1)} Ah`,
				colorClass: "copper_orange"
			},
			{
				label: this.translateService.instant("utilization.max_daily_kwh"),
				value: +maxDailyWs ? 100 : 0,
				displayValue: `${this.commonService.formatNumber(+maxDailyWs, 1)} kWh`,
				colorClass: 'copper_orange'
			},
			{
				label: this.translateService.instant("utilization.max_daily_kw"),
				value: +maxDailyKw ? 100 : 0,
				displayValue: `${this.commonService.formatNumber(+maxDailyKw, 1)} kW`,
				colorClass: 'copper_orange'
			}
		];

		const averageDailyAhData = this.printData?.[this.printSelectedPeriod]?.[this.siteDashboardService.daysFilters[0].value]?.avg_daily_as || [];
		const averageDailyAh = averageDailyAhData?.reduce((sum: number, device: { value: number }) => sum + device.value, 0) / 3600;
		const averageDailyAhPercentage = (+maxDailyAh && +averageDailyAh.toFixed(1) ? Math.round(averageDailyAh / +maxDailyAh * 100) : 0);

		const averageDailyWsData = this.printData?.[this.printSelectedPeriod]?.[this.siteDashboardService.daysFilters[0].value]?.avg_daily_ws || [];
		const averageDailyWs = averageDailyWsData?.reduce((sum: number, device: { value: number }) => sum + device.value, 0) / 3600000;
		const averageDailyWsPercentage = (+maxDailyWs && +averageDailyWs.toFixed(1) ? Math.round(averageDailyWs / +maxDailyWs * 100) : 0);

		const averageDailyKwData = this.printData?.[this.printSelectedPeriod]?.[this.siteDashboardService.daysFilters[0].value]?.avg_daily_kw || [];
		const averageDailyKw = averageDailyKwData?.reduce((sum: number, device: { value: number }) => sum + device.value, 0);
		const averageDailyKwPercentage = (+maxDailyKw && +averageDailyKw.toFixed(1) ? Math.round(averageDailyKw / +maxDailyKw * 100) : 0);

		this.printAverageDailyUsage = [
			{
				label: this.translateService.instant("utilization.avg_daily_ah"),
				value: averageDailyAhPercentage,
				displayValue: `${this.commonService.formatNumber(averageDailyAh, 1)} Ah`,
				colorClass: "cornflower_blue",
				percentage: averageDailyAhPercentage,
				showPercentage: true,
			},
			{
				label: this.translateService.instant("utilization.avg_daily_kwh"),
				value: averageDailyWsPercentage,
				displayValue: `${this.commonService.formatNumber(averageDailyWs, 1)} kWh`,
				colorClass: 'cornflower_blue',
				percentage: averageDailyWsPercentage,
				showPercentage: true,
			},
			{
				label: this.translateService.instant("utilization.avg_daily_kw"),
				value: averageDailyKwPercentage,
				displayValue: `${this.commonService.formatNumber(averageDailyKw, 1)} kW`,
				colorClass: 'cornflower_blue',
				percentage: averageDailyKwPercentage,
				showPercentage: true,
			}
		];

		const kwHeatData = [];
		const kwhHeatData = [];

		if (this.device?.mac_address && heatData?.data) {
			for (const day in heatData.data) {
				const data = heatData.data[day];

				const date = this.siteDashboardService.toLocalDate(+day);

				kwHeatData.push([
					date,
					1,
					data.max_kw_status === 'low' ? 'color: #87CEEB' : 'color: #FF6B6B',
					`<strong>${data.max_kw_status}%</strong>`
				]);

				kwhHeatData.push([
					date,
					1,
					data.max_kwh_status === 'low' ? 'color: #A0826E' : 'color: #87CEEB',
					`<strong>${data.max_kwh_status}%</strong>`
				]);
			}

			this.kwHeatData = kwHeatData;
			this.kwhHeatData = kwhHeatData;

			const baseKwOptions = this.siteDashboardService.buildHeatChartOptions(kwHeatData);
			const baseKwhOptions = this.siteDashboardService.buildHeatChartOptions(kwhHeatData);

			// Force explicit width for print (hidden container has no layout width)
			const printChartOverrides: any = {
				width: 1200,
				chartArea: { width: '95%' },
			};

			// Show months only on x-axis for long periods (> 60 data points)
			if (kwHeatData.length > 60) {
				printChartOverrides.hAxis = {
					...baseKwOptions.hAxis,
					format: 'MMM yyyy',
				};
			}

			this.kwChartOptions = { ...baseKwOptions, ...printChartOverrides };
			this.kwhChartOptions = {
				...baseKwhOptions,
				...printChartOverrides,
				...(kwHeatData.length > 60 ? { hAxis: { ...baseKwhOptions.hAxis, format: 'MMM yyyy' } } : {}),
			};

			this.max_charger_daily_kw = heatData.max_charger_daily_kw;
			this.min_charger_daily_kwh = heatData.min_charger_daily_kwh;
			this.operation_weeks = heatData.operation_weeks;

			if (!from || !to)
				this.operation_weeks = 0;
		}

		this.noDataForPrintMetrics = !averageDailyAhData.length &&
			!averageDailyWsData.length &&
			!averageDailyKwData.length;

		this.noDataForPrintHeat = !kwHeatData.length && !kwhHeatData.length;

		this.totalChargers = averageDailyAhData.length;

		this.printModal.hide();

		// Render the hidden print section now, and again right before the click: ngxPrint
		// reads #to-print-content synchronously inside its click handler, so without an
		// explicit flush it copies the previous render and the printout lags one print behind.
		this.cdr.detectChanges();

		setTimeout(() => {
			if (this.hiddenPrintBtn && this.hiddenPrintBtn.nativeElement) {
				this.cdr.detectChanges();
				this.hiddenPrintBtn.nativeElement.click();
			}
		}, 200);
	}

	ngOnDestroy() {
		this.siteSub?.unsubscribe();
		this.getDataSub?.unsubscribe();
		this.getUtilizationDataSub?.unsubscribe();
		this.printSub?.unsubscribe();
		this.printGetUtilizationDataSub?.unsubscribe();
	}
}